import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import invariant from "ts-invariant";

import { useDeleteExercise, usePostExercise, usePostFile, usePutExercise } from "api/services/content/hooks";
import { getDurationIcon, getDurationTipText } from "api/services/content/hooks/utils";
import type { ExerciseItem } from "api/services/content/schemas/exercises";
import { type ExerciseFormData, ExerciseFormDataSchema } from "api/services/content/schemas/exercises";
import type { Equipment } from "api/services/content/schemas/shared";
import { useProfileContext } from "contexts/ProfileContext";
import { ColumnContainer, ConfirmTermsCheckbox, FormPageContainer, MediaField } from "routes/library/components";
import { Form, FormPageInnerContainer } from "routes/library/components/styles";
import { FormState } from "routes/patients/PatientProfile/components/PatientHeader/PatientHeaderInfo/PatientDataList/TreatmentSwitch/FormState";
import { ButtonContainer } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryItem/LibraryItem";
import { BaseButton } from "shared/atoms/BaseButton";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { Notification } from "shared/atoms/Notification";
import { Tooltip } from "shared/atoms/Tooltip";
import { DeleteIcon } from "shared/icons";
import { sortAlphabetically } from "utils/array/sort";
import { reportError } from "utils/errorReporting";
import {
  useDurationUnitOptions,
  useEquipmentOptions,
  useMovementOptions,
  usePositionOptions,
  usePrimaryFunctionOptions,
  useTargetJointOptions,
} from "utils/form";
import { useNavigationWarning } from "utils/hooks";
import { translateDurationUnit, translateExerciseEquipment, translatePainLocation } from "utils/translations";
import { waitForFile } from "utils/url/waitForFile";

import { PreviewColumn } from "./components";
import type { PreviewTab } from "./components/ImageAndVideoContainer";
import { MetaFieldsContainer } from "./components/styles";
import { getDefaultValues } from "./components/utils";

interface Props {
  exercise?: ExerciseItem;
}

export const ExerciseForm: React.FC<Props> = ({ exercise }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  const durationUnitOptions = useDurationUnitOptions();
  const equipmentOptions = useEquipmentOptions();
  const movementOptions = useMovementOptions();
  const positionOptions = usePositionOptions();
  const primaryFunctionOptions = usePrimaryFunctionOptions();
  const targetJointOptions = useTargetJointOptions();
  const isEditing = !!exercise;
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState(isEditing);
  const [confirmedTerms, setConfirmedTerms] = useState(false);
  const [previewTab, setPreviewTab] = useState<PreviewTab>(exercise?.levels[0]?.video_file_url ? "video" : "image");
  const postFile = usePostFile();
  const postExercise = usePostExercise();
  const putExercise = usePutExercise();
  const deleteExercise = useDeleteExercise();

  invariant(profile, "Profile is required");

  const form = useForm<ExerciseFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues({ exercise, locale: profile.locale }),
    resolver: zodResolver(ExerciseFormDataSchema),
  });
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
  } = form;

  useNavigationWarning({ warn: isDirty });

  const handleUploadFile = async (file: File) => {
    setIsLoading(true);
    setPreviewTab(file.type === "video/mp4" ? "video" : "image");

    try {
      const result = await postFile.mutateAsync({
        file,
      });
      const { objectUrl } = result;

      // waitForFile is to ensure that the file is fully processed and available
      // before we set the url in the form. Adjust the maxAttempts and delayMs
      // in waitForFile args as needed based on expected processing time.

      if (file.type.startsWith("image/")) {
        await waitForFile({ url: objectUrl, maxAttempts: 100, delayMs: 3000 });
        form.setValue("levels.0.image_url", objectUrl, { shouldDirty: true });
        form.trigger();
        return;
      }
      if (file.type === "video/mp4") {
        await waitForFile({ url: objectUrl, maxAttempts: 100, delayMs: 6000 });
        form.setValue("levels.0.video.video_file_url", objectUrl.replace(".mp4", "/videos/video.m3u8"), {
          shouldDirty: true,
        });
        form.trigger();
        const thumb = objectUrl.replace("/videos/video.m3u8", "/thumbs/thumb_screen.0000000.jpg");
        await waitForFile({ url: thumb, maxAttempts: 100, delayMs: 6000 });
        form.setValue("levels.0.video.video_thumbnail_file_url", thumb, { shouldDirty: true });
        return;
      }
    } catch (error) {
      reportError("File upload failed", error as Error);
    } finally {
      setIsLoading(false);
      if (form.getValues("levels.0.video.video_file_url")) {
        setPreviewTab("video");
      }
    }
  };

  const onSubmit = handleSubmit(formData => {
    if (isEditing) {
      putExercise
        .mutateAsync({
          ...formData,
          exerciseID: exercise.id,
          exerciseLevelID: exercise.levels[0].id,
          exerciseLevelTranslationID: exercise.levels[0].translation_id,
        })
        .then(({ tempExercise }) => {
          navigate("/library/your-library/confirmation", {
            state: {
              action: "edited",
              type: "exercises",
              item: { ...tempExercise, new: true },
            },
          });
        });
    } else {
      postExercise.mutateAsync(formData).then(({ tempExercise }) => {
        navigate("/library/your-library/confirmation", {
          state: {
            action: "created",
            type: "exercises",
            item: { ...tempExercise, new: true },
          },
        });
      });
    }
  });

  const onDelete = () => {
    if (exercise) {
      deleteExercise.mutateAsync(
        { exerciseID: exercise.id },
        {
          onSuccess: () => {
            navigate("/library/your-library/exercises");
          },
        }
      );
    }
  };

  const translations = {
    button: {
      cancel: t("buttons.cancel"),
      create_exercise: t("buttons.create_exercise"),
      edit_exercise: t("buttons.edit_exercise"),
      upload_media: t("buttons.upload_image_and_video"),
      upload_media_tooltip: t("buttons.upload_image_and_video_tooltip"),
    },
    formState: {
      error: t("library.exercises.form.error"),
      loading: t("library.exercises.form.loading"),
      success: t("library.exercises.form.success"),
    },
    about: t("library.exercises.fields.about"),
    duration_unit: t("library.exercises.fields.duration_unit"),
    equipment: t("library.exercises.fields.equipment"),
    movement: t("library.exercises.fields.movement"),
    position: t("library.exercises.fields.position"),
    primary_function: t("library.exercises.fields.primary_function"),
    reps: translateDurationUnit(form.watch("levels.0.duration_unit")),
    sets: t("library.exercises.fields.sets"),
    target_joints: t("library.exercises.fields.target_joints"),
    tip_description: t("library.exercises.fields.tip_description"),
    title: t("library.exercises.fields.title"),
  };

  const isDisabled = postExercise.isLoading || putExercise.isLoading || !isDirty;
  const isLoadingAPI = postExercise.isLoading || putExercise.isLoading;
  const watchImage = form.watch("levels.0.image_url");

  const formValues = {
    levels: [
      {
        about: form.watch("levels.0.translations.0.about") || translations.about,
        caption_file_url: null,
        image_url: watchImage || null,
        instructions_icon_name: getDurationIcon(form.watch("levels.0.duration_unit")),
        instructions_text2: getDurationTipText(
          form.watch("levels.0.duration_unit"),
          form.watch("levels.0.translations.0.locale").split("-")[0]
        ),
        reps: form.watch("levels.0.reps"),
        sets: form.watch("levels.0.sets"),
        tip_description: form.watch("levels.0.translations.0.tip_description"),
        tip_title: form.watch("levels.0.translations.0.tip_title"),
        title: form.watch("levels.0.translations.0.title") || translations.title,
        video_file_url: form.watch("levels.0.video.video_file_url") || null,
        video_thumbnail_file_url: form.watch("levels.0.video.video_thumbnail_file_url") || null,
      },
    ],
  };

  return (
    <FormPageContainer
      header={isEditing ? t("library.exercises.form.edit_header") : t("library.exercises.form.create_header")}
      headerContent={
        isEditing && (
          <ButtonContainer
            onClick={e => {
              e.stopPropagation();
              setShowDialog(true);
            }}
          >
            <DeleteIcon />
          </ButtonContainer>
        )
      }
    >
      <FormPageInnerContainer>
        <ColumnContainer heading={t("library.exercises.form.header")}>
          <FormProvider {...form}>
            <Form onSubmit={onSubmit} data-testid="exercise-form" noValidate>
              <Controller
                name="levels.0.translations.0.title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={translations.title}
                    error={!!errors.levels?.[0]?.translations?.[0]?.title}
                    helperText={!!errors.levels?.[0]?.translations?.[0]?.title && t("errors.field.required")}
                  />
                )}
              />

              <Controller
                name="target_joints"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.target_joints}>
                    <InputLabel>{translations.target_joints}</InputLabel>
                    <Select
                      {...field}
                      multiple
                      error={!!errors.target_joints}
                      input={<OutlinedInput label={translations.target_joints} />}
                      renderValue={selected => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map(value => (
                            <Chip key={value} label={translatePainLocation({ painLocation: value })} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {sortAlphabetically({ list: targetJointOptions }).map(joint => (
                        <MenuItem key={joint.value} value={joint.value}>
                          {joint.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.target_joints && <FormHelperText>{t("errors.field.required")}</FormHelperText>}
                  </FormControl>
                )}
              />

              <MetaFieldsContainer>
                <Controller
                  name="primary_function"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel>{translations.primary_function}</InputLabel>
                      <Select {...field} label={translations.primary_function}>
                        {sortAlphabetically({ list: primaryFunctionOptions }).map(primaryFunction => (
                          <MenuItem key={primaryFunction.value} value={primaryFunction.value}>
                            {primaryFunction.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                <Controller
                  name="levels.0.position"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel>{translations.position}</InputLabel>
                      <Select {...field} label={translations.position}>
                        {sortAlphabetically({ list: positionOptions }).map(position => (
                          <MenuItem key={position.value} value={position.value}>
                            {position.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                <Controller
                  name="levels.0.movement"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel>{translations.movement}</InputLabel>
                      <Select {...field} label={translations.movement}>
                        {sortAlphabetically({ list: movementOptions }).map(movement => (
                          <MenuItem key={movement.value} value={movement.value}>
                            {movement.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </MetaFieldsContainer>

              <Controller
                name="levels.0.equipment"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.levels?.[0]?.equipment}>
                    <InputLabel>{translations.equipment}</InputLabel>
                    <Select
                      {...field}
                      multiple
                      error={!!errors.levels?.[0]?.equipment}
                      input={<OutlinedInput label={translations.equipment} />}
                      renderValue={selected => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(selected as Equipment[]).map(value => (
                            <Chip key={value} label={translateExerciseEquipment(value)} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {sortAlphabetically({ list: equipmentOptions }).map(equipment => (
                        <MenuItem key={equipment.value} value={equipment.value}>
                          {equipment.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.levels?.[0]?.equipment && <FormHelperText>{t("errors.field.required")}</FormHelperText>}
                  </FormControl>
                )}
              />

              <Controller
                name="levels.0.translations.0.about"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={translations.about}
                    rows={3}
                    multiline
                    error={!!errors.levels?.[0]?.translations?.[0]?.about}
                    helperText={!!errors.levels?.[0]?.translations?.[0]?.about && t("errors.field.required")}
                  />
                )}
              />

              <Controller
                name="levels.0.duration_unit"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <InputLabel>{translations.duration_unit}</InputLabel>
                    <Select {...field} label={translations.duration_unit}>
                      {sortAlphabetically({ list: durationUnitOptions }).map(durationUnit => (
                        <MenuItem key={durationUnit.value} value={durationUnit.value}>
                          {durationUnit.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Box display="flex" gap={2}>
                <Controller
                  name="levels.0.reps"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={translations.reps}
                      type="number"
                      fullWidth
                      error={!!errors.levels?.[0]?.reps}
                      helperText={!!errors.levels?.[0]?.reps && t("errors.field.required")}
                    />
                  )}
                />

                <Controller
                  name="levels.0.sets"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={translations.sets}
                      type="number"
                      fullWidth
                      error={!!errors.levels?.[0]?.sets}
                      helperText={!!errors.levels?.[0]?.sets && t("errors.field.required")}
                      value={field.value || ""}
                      onChange={e => field.onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                    />
                  )}
                />
              </Box>

              <Controller
                name="levels.0.translations.0.tip_description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={translations.tip_description}
                    rows={3}
                    multiline
                    onChange={e => {
                      field.onChange(e);
                      const { value } = e.target;
                      const currentTipTitle = form.getValues("levels.0.translations.0.tip_title");
                      if (value && value.trim() !== "") {
                        if (!currentTipTitle || currentTipTitle.trim() === "") {
                          form.setValue("levels.0.translations.0.tip_title", t("library.exercises.fields.tip_title"));
                        }
                      } else {
                        form.setValue("levels.0.translations.0.tip_title", "");
                      }
                    }}
                  />
                )}
              />

              <ConfirmTermsCheckbox checked={confirmedTerms} onChange={e => setConfirmedTerms(e.target.checked)} />

              <Tooltip title={translations.button.upload_media_tooltip} placement="top" arrow>
                <div>
                  <MediaField
                    accept="image/*, .mp4"
                    error={
                      !!errors.levels?.[0]?.image_url || !!errors.levels?.[0]?.video?.video_file_url
                        ? t("errors.field.image_or_video_required")
                        : undefined
                    }
                    text={translations.button.upload_media}
                    isDisabled={!confirmedTerms}
                    isLoading={isLoading}
                    handleUploadFile={event => {
                      const { files } = event.target;
                      if (files && files.length > 0) {
                        handleUploadFile(files[0]);
                      }
                    }}
                  />
                </div>
              </Tooltip>

              {postFile.isError && (
                <Notification variant="danger" style={{ marginBottom: "8px" }}>
                  {t("errors.could_not_upload_file")}
                </Notification>
              )}

              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Box display="flex" flexDirection="column" justifyContent="center" width="100%" gap={2}>
                  <BaseButton
                    type="submit"
                    text={isEditing ? translations.button.edit_exercise : translations.button.create_exercise}
                    disabled={isDisabled}
                    loading={isLoadingAPI}
                  />

                  {isEditing && (
                    <BaseButton text={translations.button.cancel} variant="tertiaryBlue" onClick={() => navigate(-1)} />
                  )}
                </Box>

                <FormState
                  formState={postExercise.status || putExercise.status}
                  messages={{
                    error: translations.formState.error,
                    loading: translations.formState.loading,
                    success: translations.formState.success,
                  }}
                />
              </Box>
            </Form>
          </FormProvider>
        </ColumnContainer>

        <PreviewColumn
          exercise={formValues}
          image_url={watchImage || null}
          isLoading={isLoading}
          previewTab={previewTab}
        />
      </FormPageInnerContainer>

      <ConfirmationPopup
        isOpen={showEditWarning}
        title={t("library.exercises.form.edit_exercise_warning_title")}
        body={t("library.exercises.form.edit_exercise_warning_body")}
        onClickOutside={() => setShowEditWarning(false)}
        onConfirm={() => setShowEditWarning(false)}
      />

      <ConfirmationPopup
        isOpen={showDialog}
        title={t("library.lessons.form.delete_exercise_title")}
        onCancel={() => setShowDialog(false)}
        onClickOutside={() => setShowDialog(false)}
        onConfirm={onDelete}
      />
    </FormPageContainer>
  );
};
